import React, {useState, useEffect} from 'react';
import {
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  makeStyles,
  InputAdornment,
} from '@material-ui/core';

import {Controller, useFormContext} from 'react-hook-form';

import {useAddressPickup} from '../../utils/use-addres-pick-up';
import {priceFormat} from '../../../../utils/number';
import {useStoreCurrency} from '../../../currency/utils';
import {DELIVERY_FORM_FIELDS} from './constant';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import {useGetCurrency} from '../../utils';
import {colors} from '../../../../assets/styles/colors';

const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
  },
  pickupMethodItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
    paddingBlock: '.75rem',
    borderTop: '1px solid #ccc',

    '&:first-child': {
      paddingTop: 0,
      borderTop: 'none',
    },
  },
  radioGroupContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  pickupMethodRadioContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pickupMethodRadio: {
    flexGrow: 1,
  },
  subtitle: {
    fontWeight: 600,
    fontSize: '.875rem',
    color: colors.grey700,
    marginBottom: theme.spacing(1),
  },
  errorMessage: {
    color: colors.danger,
    fontSize: '0.75rem',
  },
}));

type Props = {
  isEditing?: boolean,
};

export default function PickupMethod(props: Props) {
  const classes = useStyles();
  const {isEditing} = props;
  const currency = useGetCurrency();
  const storeCurrency = useStoreCurrency();
  const {pickupMethods} = useAddressPickup();
  const [pickupSelected, setPickupSelected] = useState(null);
  const {register, errors, setValue} = useFormContext();

  const handlePriceChange = event => {
    const price = Number(event.target.value);
    setValue(DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST, price);
  };

  useEffect(() => {
    if (pickupSelected) {
      setValue(
        DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST,
        Number(pickupSelected.shippingRates[0].price),
        {shouldValidate: true}
      );
    }
  }, [setValue, pickupSelected]);

  return (
    <div>
      <Typography className={classes.title}>Recoger en tienda</Typography>
      <Controller
        name={DELIVERY_FORM_FIELDS.PICKUP_METHOD}
        rules={{required: 'Seleccione el lugar de recojo'}}
        render={({onChange, onBlur, value, name, ref}) => (
          <RadioGroup
            className={classes.radioGroupContainer}
            value={value}
            onChange={e => {
              const pickupMethodId = e.target.value;
              const pickupMethod = pickupMethods.find(
                method => method.id === pickupMethodId
              );

              onChange(pickupMethod);

              if (isEditing) {
                setPickupSelected(pickupMethod);
              }
            }}
            onBlur={onBlur}
          >
            {pickupMethods.map(pickupMethod => {
              const pickUpPrice = priceFormat(
                pickupMethod.shippingRates[0]?.price,
                storeCurrency.symbol
              );
              return (
                <div key={pickupMethod.id} className={classes.pickupMethodItem}>
                  <div className={classes.pickupMethodRadioContainer}>
                    <FormControlLabel
                      value={pickupMethod.id}
                      className={classes.pickupMethodRadio}
                      control={<Radio color="primary" />}
                      label={pickupMethod.displayName}
                    />
                    <span className={classes.pickupMethodItemPrice}>
                      {pickUpPrice}
                    </span>
                  </div>

                  <Typography variant="body1">
                    {pickupMethod.address1 && `${pickupMethod.address1}, `}
                    {pickupMethod.address2 && `${pickupMethod.address2}, `}
                    {pickupMethod.admin1 &&
                      `${pickupMethod.admin1.admin1Name},&nbsp;`}
                    {pickupMethod.admin2 &&
                      `${pickupMethod.admin2.admin2Name},&nbsp;`}
                    {pickupMethod.admin3 && pickupMethod.admin3.admin3Name}
                  </Typography>

                  {pickupMethod.description && (
                    <Typography variant="caption">
                      {pickupMethod.description}
                    </Typography>
                  )}
                </div>
              );
            })}
          </RadioGroup>
        )}
      />

      {errors[DELIVERY_FORM_FIELDS.PICKUP_METHOD] && (
        <span className={classes.errorMessage}>
          {errors[DELIVERY_FORM_FIELDS.PICKUP_METHOD].message}
        </span>
      )}

      {isEditing && (
        <>
          <Typography className={classes.subtitle}>Precio</Typography>
          <CustomTextField
            className={classes.textField}
            name={DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST}
            label="Precio de delivery"
            inputRef={register()}
            onChange={handlePriceChange}
            disabled={!pickupSelected}
            InputProps={{
              className: classes.textInput,
              startAdornment: (
                <InputAdornment position="start">
                  {currency && currency.symbol}
                </InputAdornment>
              ),
            }}
            helperText={
              errors[DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST]?.message
            }
            error={!!errors[DELIVERY_FORM_FIELDS.MANUAL_DELIVERY_COST]}
            InputLabelProps={{className: classes.textInputLabel}}
            type="number"
            inputProps={{step: '.1'}}
          />
        </>
      )}
    </div>
  );
}
